// src/components/browser/taskbar/ServerStatus.tsx
//
// Indicador visual de status do servidor — para usar dentro da TaskBar.
//
// Mostra:
//   • Ponto verde pulsante → online
//   • Ponto laranja girando → verificando
//   • Ponto vermelho → offline (com tooltip do erro)
//
// Uso na TaskBar.tsx:
//   import ServerStatus from './ServerStatus';
//   // dentro do <Toolbar>:
//   <ServerStatus />

import { Box, CircularProgress, Tooltip, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useServerHealth from './hooks/UseServerHealth';


function ServerStatus() {
    const theme = useTheme();
    const { online, checando, ultimoCheck, erro, verificarAgora } = useServerHealth({
        intervalo: 30_000,
        notificar: true,
    });

    const cor = checando
        ? theme.palette.warning.main
        : online
        ? '#4caf50'
        : theme.palette.error.main;

    const label = checando
        ? 'Verificando servidor...'
        : online
        ? `Online${ultimoCheck ? ' · ' + ultimoCheck.toLocaleTimeString('pt-BR') : ''}`
        : `Offline · ${erro ?? 'Sem resposta'}`;

    return (
        <Tooltip title={label} arrow>
            <Box
                onClick={verificarAgora}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.6,
                    cursor: 'pointer',
                    px: 1,
                    py: 0.4,
                    borderRadius: 2,
                    transition: 'background 0.2s',
                    '&:hover': { bgcolor: 'action.hover' },
                }}
            >
                
                    <Box
                        sx={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            bgcolor: cor,
                            // Animação de pulso apenas quando online
                            ...(online && !checando && {
                                animation: 'pulse 2s infinite',
                                '@keyframes pulse': {
                                    '0%':   { boxShadow: `0 0 0 0 ${cor}88` },
                                    '70%':  { boxShadow: `0 0 0 5px ${cor}00` },
                                    '100%': { boxShadow: `0 0 0 0 ${cor}00` },
                                },
                            }),
                        }}
                    />
                
                <Typography
                    variant="caption"
                    sx={{
                        color: cor,
                        fontWeight: 600,
                        lineHeight: 1,
                        display: { xs: 'none', sm: 'block' },
                    }}
                >
                    {online ? 'Online' : 'Offline'}
                </Typography>
            </Box>
        </Tooltip>
    );
}

export default ServerStatus;